import { parseServerDate } from './time'
import type { SendingDomainRateLimitsDto } from '@/types/sendingDomains'

// What every ORB-953 screen (the two sending-domain surfaces, the activation editor and the broadcast page) prints
// the same way: counts, rates, limits and days, a step's limits and the move-up rule. What only the two
// sending-domain surfaces say is in sendingDomainStatus. Screens read the numbers from the API; nothing here restates
// a warm-up rule.

const counts = new Intl.NumberFormat('en-US')

// Fixed rather than Intl: ICU versions disagree on September ("Sep" or "Sept").
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** 1000 -> "1,000". */
export function formatCount(value: number): string {
  return counts.format(value)
}

/** A rate as the screens print it: bounces to one decimal ("2.4%"), complaints to two ("0.04%"). */
export function formatRate(percent: number, decimals: 1 | 2): string {
  return `${percent.toFixed(decimals)}%`
}

/** A limit from RateLimits without trailing zeros: 2 -> "2%", 0.04 -> "0.04%". */
export function formatLimit(percent: number): string {
  return `${Number(percent.toFixed(2))}%`
}

/** "Step 4 of 11: up to 700 a day, 50 an hour." (no hourly part on steps 1 to 3). */
export function stepLimitsSentence(step: number, lastStep: number, dailyLimit: number, hourlyLimit?: number | null): string {
  const hourly = hourlyLimit ? `, ${formatCount(hourlyLimit)} an hour` : ''
  return `Step ${step} of ${lastStep}: up to ${formatCount(dailyLimit)} a day${hourly}.`
}

/** "Moves up after a day that sends at least 350, while hard bounces stay at 2% or less and complaints at 0.04% or less." */
export function moveUpRuleSentence(qualifyingVolume: number, limits: SendingDomainRateLimitsDto): string {
  return `Moves up after a day that sends at least ${formatCount(qualifyingVolume)}, while hard bounces stay at `
    + `${formatLimit(limits.MaxHardBouncePercentToMoveUp)} or less and complaints at `
    + `${formatLimit(limits.MaxComplaintPercentToMoveUp)} or less.`
}

/** A UTC instant in the viewer's own time with its zone: "02:00 CEST". */
export function localTimeWithZone(utc: string): string {
  const date = parseServerDate(utc)
  if (!date) {
    console.error('Unparseable timestamp', utc)
    return ''
  }
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short' })
}

/** An instant's day in the viewer's own time: "Sat 26 Sep" (e.g. when a domain stopped). */
export function localShortDay(utc: string): string {
  const date = parseServerDate(utc)
  if (!date) {
    console.error('Unparseable timestamp', utc)
    return ''
  }
  return `${WEEKDAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]}`
}

/**
 * A warm-up day's label. Days are UTC days (a domain's limit resets at midnight UTC), so the label is the
 * UTC date, never shifted into the viewer's zone; pair it with localTimeWithZone for when it starts.
 */
export function utcDayLabel(dayStartUtc: string): string {
  const date = parseServerDate(dayStartUtc)
  if (!date) {
    console.error('Unparseable timestamp', dayStartUtc)
    return ''
  }
  return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`
}
